import { useContext } from 'react';
import JobsContext from './context/JobsContext';

export type ThemeMode = 'light' | 'dark';

export interface Palette {
  canvas: string;
  surface: string;
  surfaceMuted: string;
  elevated: string;
  border: string;
  textPrimary: string;
  textSecondary: string;
  textMuted: string;
  textOnDark: string;
  textOnDarkMuted: string;
  primary: string;
  primaryPressed: string;
  primaryDark: string;
  secondary: string;
  appliedBg: string;
  appliedText: string;
  favorite: string;
  danger: string;
  navBar: string;
  navBarBorder: string;
}

const lightPalette: Palette = {
  canvas: '#F2F3F5',
  surface: '#FFFFFF',
  surfaceMuted: '#F0F1F3',
  elevated: '#0F1115',
  border: '#E5E7EB',
  textPrimary: '#1E2229',
  textSecondary: '#555555',
  textMuted: '#8A8F98',
  textOnDark: '#FFFFFF',
  textOnDarkMuted: '#9AA0A6',
  primary: '#4effa1',
  primaryPressed: '#3ce58d',
  primaryDark: '#1F4D3A',
  secondary: '#2196f3',
  appliedBg: '#D9F9E6',
  appliedText: '#0E8A4A',
  favorite: '#EF4444',
  // Material Red 800 — deeper than the dark-mode red so destructive/error TEXT
  // clears WCAG AA (~5:1) on light surfaces; #F44336 was only ~3.3:1.
  danger: '#C62828',
  navBar: '#0F1115',
  navBarBorder: '#1A1D24',
};

const darkPalette: Palette = {
  canvas: '#0F1115',
  surface: '#1A1D24',
  surfaceMuted: '#23262E',
  elevated: '#0F1115',
  border: '#2A2E36',
  textPrimary: '#F2F3F5',
  textSecondary: '#C7CAD1',
  textMuted: '#9AA0A6',
  textOnDark: '#FFFFFF',
  textOnDarkMuted: '#9AA0A6',
  primary: '#4effa1',
  primaryPressed: '#3ce58d',
  primaryDark: '#1F4D3A',
  secondary: '#2196f3',
  appliedBg: '#1B3F2C',
  appliedText: '#4effa1',
  favorite: '#EF4444',
  danger: '#F44336',
  navBar: '#0B0D11',
  navBarBorder: '#1A1D24',
};

export const palettes: Record<ThemeMode, Palette> = {
  light: lightPalette,
  dark: darkPalette,
};

export const getPalette = (mode: ThemeMode): Palette => palettes[mode];

export function useThemePalette(): Palette {
  const { themeMode } = useContext(JobsContext);
  return palettes[themeMode];
}

export const spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  xxl: 32,
};

export const radius = {
  sm: 8,
  md: 12,
  lg: 16,
  pill: 999,
};

export const shadow = {
  card: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 6,
    elevation: 2,
  },
  fab: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
};

export default { palettes, getPalette, spacing, radius, shadow };
